/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { EditBusinessHours } from "~/components/BasicCustomerInfo/Edit/EditBusinessHours";

import {
  BasicInfoEditModeType,
  BasicInfoLoadingType,
  Customer,
} from "~/components/BasicCustomerInfo/types";
import { InlineEditDropdown } from "~/components/InlineEdit/InlineEditDropdown";
import { InlineEditMultiSelect } from "~/components/InlineEdit/InlineEditMultiSelect";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { DetailRow } from "~/components/Utils/DetailRow";
import { cloudProvidersList } from "~/constants/cloudProviders";
import { currencyOptions } from "~/constants/currencyList";
import { maxLength } from "~/tools/validation/validators";

type CustomerDetailsProps = {
  item: Customer;
  onSubmit: (values: Customer) => Promise<void>;
  isLoading: BasicInfoLoadingType;
  setIsLoading: React.Dispatch<React.SetStateAction<BasicInfoLoadingType>>;
  isEditMode: BasicInfoEditModeType;
  setIsEditMode: React.Dispatch<React.SetStateAction<BasicInfoEditModeType>>;
  canEdit?: boolean;
};

function BusinessDetails({
  item,
  onSubmit,
  isLoading,
  setIsLoading,
  isEditMode,
  setIsEditMode,
  canEdit,
}: CustomerDetailsProps) {
  return (
    <>
      <DetailRow
        title="Business ID"
        tooltip="The code given to businesses and organisations for tax administration"
      >
        <InlineEditText
          name="businessId"
          defaultValue={item.info.businessId}
          onSubmit={(businessId) =>
            onSubmit({ ...item, info: { ...item.info, businessId } })
          }
          canEdit={canEdit}
          validation={maxLength(36)}
        />
      </DetailRow>

      <DetailRow
        title="SF Account ID"
        tooltip="The code should be visible in all customer documents so everyone can easily correlate"
      >
        <InlineEditText
          name="salesforceId"
          defaultValue={item.info.salesforceId}
          href={"https://nordcloud.my.salesforce.com/" + item.info.salesforceId}
          onSubmit={(salesforceId) =>
            onSubmit({ ...item, info: { ...item.info, salesforceId } })
          }
          canEdit={canEdit}
          validation={maxLength(36)}
        />
      </DetailRow>

      <DetailRow title="Cloud Providers">
        <InlineEditMultiSelect
          options={cloudProvidersList}
          defaultValue={item.info.providers || []}
          onSubmit={(providers) =>
            onSubmit({ ...item, info: { ...item.info, providers } })
          }
          providerIcons
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Currency">
        <InlineEditDropdown
          name="currency"
          options={currencyOptions}
          defaultValue={item.info.currency}
          onSubmit={(currency) =>
            onSubmit({ ...item, info: { ...item.info, currency } })
          }
          canEdit={canEdit}
        />
      </DetailRow>

      <DetailRow title="Business Hours">
        <EditBusinessHours
          item={item}
          onSubmit={onSubmit}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          canEdit={canEdit}
        />
      </DetailRow>
    </>
  );
}

export { BusinessDetails };
